import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchDBUDataWithFiltersData } from 'service/service';
import moment from 'moment';
import ComputeFlatTableBase from './ComputeFlatTableBase';
import { QueryRule, FieldOption, formatCompactNumber } from 'utils/adb-common-util';

const COLUMN_TO_API_MAP: Record<string, string> = {
  'workspace_id': 'Workspace ID',
  'workspace_name': 'Workspace Name',
  'env': 'Environment',
  'computetype': 'Compute Type',
  'cluster_name': 'Cluster Name',
  'usage_date': 'Usage Date',
  'dbu': 'DBU',
  'compute_cost': 'Compute Cost',
  'total_runtime_hours': 'Total Runtime Hours',
  'workspace_url': 'Workspace URL',
  'job_name': 'Job Name',
  'cluster_id': 'Cluster ID',
  'auto_termination_minutes': 'Auto Termination (min)'
};

const DEFAULT_COLUMN_NAMES = [
  'Workspace ID', 'Workspace Name', 'Environment', 'Compute Type', 'Cluster Name', 'Usage Date', 'DBU',
  'Compute Cost', 'Total Runtime Hours', 'Workspace URL', 'Job Name', 'Cluster ID', 'Auto Termination (min)'
];

const getColumns = () => [
  { key: 'workspace_id', name: 'Workspace ID', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_id}>{row?.workspace_id || '--'}</div> },
  { key: 'workspace_name', name: 'Workspace Name', minWidth: 200, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_name}>{row?.workspace_name || '--'}</div> },
  { key: 'env', name: 'Environment', minWidth: 120, sortable: true, formatter: ({ row }: any) => <div>{row?.env?.toUpperCase() || '--'}</div> },
  { key: 'computetype', name: 'Compute Type', minWidth: 140, sortable: true, formatter: ({ row }: any) => <div>{row?.computetype || '--'}</div> },
  { key: 'cluster_name', name: 'Cluster Name', minWidth: 220, sortable: true, formatter: ({ row }: any) => <div title={row?.cluster_name}>{row?.cluster_name || '--'}</div> },
  { key: 'usage_date', name: 'Usage Date', minWidth: 140, sortable: true, formatter: ({ row }: any) => <div>{row?.usage_date ? moment.utc(row.usage_date).format('YYYY-MM-DD') : '--'}</div> },
  { key: 'dbu', name: 'DBU', minWidth: 120, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.dbu === 'number' ? formatCompactNumber(row.dbu) : row?.dbu || '--'}</div> },
  { key: 'compute_cost', name: 'Compute Cost', minWidth: 140, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.compute_cost === 'number' ? `$${row.compute_cost.toFixed(2)}` : row?.compute_cost || '--'}</div> },
  { key: 'total_runtime_hours', name: 'Total Runtime Hours', minWidth: 160, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.total_runtime_hours === 'number' ? row.total_runtime_hours.toFixed(2) : row?.total_runtime_hours || '--'}</div> },
  {
    key: 'workspace_url', name: 'Workspace URL', minWidth: 200, sortable: true, formatter: ({ row }: any) => {
      const fullUrl = row?.workspace_url && !row.workspace_url.startsWith('http') ? `https://${row.workspace_url}` : row?.workspace_url;
      let displayText = '--';
      if (fullUrl) {
        const match = fullUrl.match(/^https?:\/\/([^.]+)/);
        displayText = match ? match[1] : fullUrl;
      }
      return (
        <div title={fullUrl}>
          {fullUrl ? <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', textDecoration: 'underline' }}>{displayText}</a> : '--'}
        </div>
      );
    }
  },
  { key: 'job_name', name: 'Job Name', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div title={row?.job_name}>{row?.job_name || '--'}</div> },
  { key: 'cluster_id', name: 'Cluster ID', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div title={row?.cluster_id}>{row?.cluster_id || '--'}</div> },
  { key: 'auto_termination_minutes', name: 'Auto Termination (min)', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.auto_termination_minutes !== null && row.auto_termination_minutes !== undefined ? row.auto_termination_minutes : '--'}</div> }
];

const RunTimeTableWS = () => {
  const location = useLocation();
  const navigationState = location.state;

  const serviceFuncWrapper = useCallback(async (params: any) => {
    const {
      startDate,
      endDate,
      env,
      sector,
      location: locationName,
      selectedDateInString,
      page,
      pageSize,
      sortField,
      sortOrder,
      filters,
      exportType
    } = params;
    const apiFilters = [...(filters || [])];

    apiFilters.push({ operator: 'OR', field: 'Sector', value: sector !== 'all' ? sector : 'All' });
    apiFilters.push({ operator: 'OR', field: 'Environment', value: env !== 'both' ? env : 'All' });
    apiFilters.push({ operator: 'AND', field: 'Location', value: locationName !== 'ALL' ? locationName : 'All' });

    if (!apiFilters.some(f => f.field === 'Compute Type')) {
      apiFilters.push({ operator: 'OR', field: 'Compute Type', value: 'All' });
    }
    const workspaceOptionIds = apiFilters.filter(f => f.field === 'Workspace ID').map(f => f.value);
    if (workspaceOptionIds.length === 0) {
      apiFilters.push({ operator: 'AND', field: 'Workspace ID', value: 'All' });
    }

    const resp = await fetchDBUDataWithFiltersData({
      startDate,
      endDate,
      env,
      sector,
      location: locationName,
      page,
      pageSize,
      filters: apiFilters,
      selectedDateInString,
      workspaceName: undefined,
      exportType,
      sortField,
      sortOrder
    });

    if (resp?.success && resp.data && !resp.data.docs && resp.data.data) {
      resp.data.docs = resp.data.data;
      resp.data.pagination = {
        totalRecords: resp.data.pagination?.total || resp.data.data.length,
        totalPages: resp.data.pagination?.totalPages || Math.ceil((resp.data.pagination?.total || resp.data.data.length) / pageSize)
      };
    }
    return resp;
  }, []);

  const getInitialQueryFilters = useCallback((navState: any) => {
    const filters: QueryRule[] = [];
    if (navState?.contentName && navState?.contentName !== 'all') {
      filters.push({ id: '1', operator: 'AND', field: 'Workspace Name', value: navState.contentName });
    }
    if (navState?.computeType && navState?.computeType !== 'all') {
      filters.push({ id: filters.length > 0 ? '2' : '1', operator: 'AND', field: 'Compute Type', value: navState.computeType });
    }
    return filters;
  }, []);

  const mapQueryRuleToApi = useCallback((rule: QueryRule, workspaceOptions: FieldOption[]) => {
    const rawValue = rule.value?.toString() ?? '';
    const valueLower = rawValue.toLowerCase();
    if (!rawValue || valueLower === 'all' || valueLower === '') return null;

    if (rule.field === 'Workspace Name') {
      const opt = workspaceOptions.find(o =>
        o.value === rawValue ||
        (o as any).workspaceId === rawValue ||
        ((o as any).workspaceName && (o as any).workspaceName.toString().toLowerCase() === valueLower) ||
        (o.label && o.label.toString().toLowerCase() === valueLower)
      );
      if (opt) return { field: 'Workspace ID', value: (opt as any).workspaceId || opt.value };
      return { field: 'Workspace ID', value: rawValue };
    } else if (rule.field === 'Compute Type') {
      return { field: 'Compute Type', value: rule.value };
    }
    return { field: rule.field, value: rule.value };
  }, []);

  const transformRecords = useCallback((records: any[]) => records.map((r: any) => ({
    ...r,
    workspace_name: r?.workspace_name || r?.workspaceName || '--',
    env: r?.env || '--'
  })), []);

  return (
    <ComputeFlatTableBase
      serviceFunc={serviceFuncWrapper}
      pageTitle={navigationState?.dateLabel || "RunTime Details"}
      breadcrumbLabel={navigationState?.pageHeader || "Databricks Analysis"}
      breadcrumbPath="/databricksADB/workspace"
      middleBreadcrumb={navigationState?.pageSubHeader ? {
        label: navigationState.pageSubHeader,
        path: '/databricksADB/performance-dashboard',
        state: {
          pageHeader: navigationState.pageHeader,
          pageSubHeader: navigationState.pageSubHeader,
          detailedTableTitle: navigationState.detailedTableTitle,
          detailedTableType: navigationState.detailedTableType,
          contentName: navigationState.contentName,
          searchSource: navigationState.searchSource,
          filters: navigationState.filters,
          clickedStartDate: navigationState.clickedStartDate,
          clickedEndDate: navigationState.clickedEndDate
        }
      } : undefined}
      tableType={navigationState?.detailedTableType || "dbu_count-table"}
      defaultColumnNames={DEFAULT_COLUMN_NAMES}
      getColumns={getColumns}
      columnToApiMap={COLUMN_TO_API_MAP}
      preserveFilterKey="dbu_table_filters"
      isJob={false}
      getInitialQueryFilters={getInitialQueryFilters}
      mapQueryRuleToApi={mapQueryRuleToApi}
      transformRecords={transformRecords}
    />
  );
};

export default RunTimeTableWS;